import React, {
    type MutableRefObject,
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';
import classnames from 'classnames';
import { findIndex, propEq } from 'ramda';

import useTranslation from '../../../features/locale/useTranslation';
import { Icon } from '../../Icon';
import { Text } from '../../Typography';
import useScrollableShowMore from '../hooks/useScrollableShowMore';
import useScrollableShowTop from '../hooks/useScrollableShowTop';

import { type ListColumnType } from './VirtualizedList.types';

import styles from './VirtualizedList.module.scss';

type VirtualizedListProps = {
    className?: string;
    columns: ListColumnType;
    height?: number;
    highlightEnabled: boolean;
    includeShowMore?: boolean;
    includeShowTop?: boolean;
    includeTopBorder?: boolean;
    includeBottomBorder?: boolean;
    listClassName?: string;
    rowRenderer: React.ComponentType<any>;
    rows: any[];
    scrollToId?: {
        timestamp: number;
        id: string;
    };
    setScrollToId?: React.Dispatch<
        React.SetStateAction<{
            timestamp: number;
            id: string;
        }>
    >;
    overscan?: number;
};

let scrollTimeout: ReturnType<typeof setTimeout>;
let clearScrollTimeout: ReturnType<typeof setTimeout>;

export const VirtualizedList = forwardRef<HTMLDivElement, VirtualizedListProps>(
    (
        {
            className,
            columns,
            height = 0,
            highlightEnabled = true,
            includeShowMore = true,
            includeShowTop = false,
            includeTopBorder = true,
            includeBottomBorder = true,
            listClassName,
            overscan,
            rowRenderer,
            rows,
            scrollToId,
            setScrollToId
        }: VirtualizedListProps,
        ref
    ) => {
        const { t } = useTranslation();
        const containerRef = useRef<HTMLDivElement>(null);
        const endScrollableAreaRef = useRef<HTMLDivElement>(null);
        const startScrollableAreaRef = useRef<HTMLDivElement>(null);
        const [highlightRow, setHighlightRow] = useState(false);
        const [containerHeight, setContainerHeight] = useState(0);
        const rowVirtualizer = useVirtualizer({
            count: rows.length,
            estimateSize: useCallback(() => 50, []),
            getScrollElement: useCallback(
                () => (ref as MutableRefObject<HTMLDivElement>)?.current,
                []
            ),
            overscan: overscan ?? 15
        });

        const { handleMoreClick, showMore } = useScrollableShowMore({
            endScrollableAreaRef: endScrollableAreaRef as MutableRefObject<HTMLDivElement>,
            scrollableRef: ref as MutableRefObject<HTMLDivElement>
        });

        const { handleTopClick, showTop } = useScrollableShowTop({
            startScrollableAreaRef: startScrollableAreaRef as MutableRefObject<HTMLDivElement>,
            scrollableRef: ref as MutableRefObject<HTMLDivElement>
        });

        const handleScrollToRow = (id: string) => {
            const index = findIndex(propEq(id, 'id'), rows);
            scrollTimeout = setTimeout(() => {
                rowVirtualizer.scrollToIndex(index, { align: 'center', behavior: 'smooth' });
                if (highlightEnabled) {
                    setHighlightRow(true);
                }
            }, 100);
            clearScrollTimeout = setTimeout(() => {
                setHighlightRow(false);
                setScrollToId?.({ id: '', timestamp: 0 });
            }, 4000);
        };

        const RowRenderer = rowRenderer;

        useEffect(() => {
            if (scrollToId?.id) {
                handleScrollToRow(scrollToId.id);
            }
        }, [scrollToId?.timestamp]);

        useEffect(() => {
            // clean up timeouts and state hooks
            return () => {
                setHighlightRow(false);
                setScrollToId?.({ id: '', timestamp: 0 });
                clearTimeout(scrollTimeout);
                clearTimeout(clearScrollTimeout);
            };
        }, []);

        useEffect(() => {
            const observer = new ResizeObserver(([entry]) => {
                const rect = entry.contentRect?.height ?? 0;
                setContainerHeight(rect);
            });
            if (containerRef?.current) {
                observer.observe(containerRef.current);
            }
            return () => {
                if (containerRef?.current) {
                    observer.unobserve(containerRef.current);
                }
            };
        }, []);

        return (
            <>
                {includeShowTop && (
                    <div className={styles.topWrapper}>
                        <div
                            className={classnames(styles.more, { [styles.visible]: showTop })}
                            onClick={handleTopClick}
                            role="button"
                            tabIndex={-1}
                        >
                            <Text>{t('Top')}</Text>
                            <Icon name="s-icon-arrow-up" />
                        </div>
                    </div>
                )}
                <div
                    className={classnames(styles.autoSizer, listClassName)}
                    style={{ minHeight: height || 304 }}
                    ref={containerRef}
                >
                    <div
                        className={styles.listParent}
                        ref={ref}
                        style={{
                            height: containerHeight
                        }}
                    >
                        <div ref={startScrollableAreaRef} className={styles.startScrollableArea} />
                        <div id="section-header" />
                        <div
                            className={styles.container}
                            style={{
                                height: `${rowVirtualizer.getTotalSize()}px`
                            }}
                        >
                            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                const row = rows[virtualRow.index];
                                return (
                                    <div
                                        key={virtualRow.key}
                                        data-index={virtualRow.index}
                                        ref={rowVirtualizer.measureElement}
                                        className={classnames(styles.virtualRow, {
                                            [styles.autoAdded]: row.type === 'auto-added',
                                            [styles.noTopBorder]: !includeTopBorder,
                                            [styles.noBottomBorder]: !includeBottomBorder
                                        })}
                                        style={{
                                            transform: `translateY(${virtualRow.start}px)`,
                                            transition:
                                                highlightRow && row.id === scrollToId?.id
                                                    ? 'background 1s'
                                                    : '',
                                            background:
                                                highlightRow && row.id === scrollToId?.id
                                                    ? `var(--color-layout-surface-primary-subtle, #EEEAFA)`
                                                    : 'transparent'
                                        }}
                                    >
                                        <RowRenderer
                                            className={className}
                                            columns={columns}
                                            row={row}
                                            virtualRow={virtualRow}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <div ref={endScrollableAreaRef} className={styles.endScrollableArea} />
                    </div>
                </div>
                {includeShowMore && (
                    <div className={styles.moreWrapper}>
                        <div
                            className={classnames(styles.more, { [styles.visible]: showMore })}
                            onClick={handleMoreClick}
                            role="button"
                            tabIndex={-1}
                        >
                            <Text>{t('More')}</Text>
                            <Icon name="s-icon-arrow-down" />
                        </div>
                    </div>
                )}
            </>
        );
    }
);

VirtualizedList.displayName = 'VirtualizedList';
